/**
 * Customers list — Finvalda reconciliation flag (pr5-contract.md §2).
 *
 * A customer is flagged only when the Finvalda balance is both PRESENT and
 * FRESH (synced in the last 24h) and actually differs from our computed
 * debt by at least a cent — an old balance or a rounding-level gap must not
 * light up the whole list.
 */
const DAY_MS = 24 * 60 * 60 * 1000;
const TOLERANCE = 0.01;

export interface FinvaldaMismatchInput {
  finvaldaBalance?: number | string | null;
  finvaldaBalanceAt?: number | string | null;
  _debt?: number | string | null;
}

function toMs(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  const ms = typeof value === "number" ? value : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function toNumber(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  const num = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(num) ? num : null;
}

export function isFinvaldaMismatch(
  item: FinvaldaMismatchInput,
  now: number = Date.now()
): boolean {
  const balance = toNumber(item.finvaldaBalance);
  const balanceAt = toMs(item.finvaldaBalanceAt);
  if (balance === null || balanceAt === null) return false;
  if (now - balanceAt > DAY_MS) return false;

  const debt = toNumber(item._debt) ?? 0;
  return Math.abs(balance - debt) >= TOLERANCE;
}

/** finvaldaBalance − debt (pr3b-contract.md §3), for the flag's tooltip. */
export function finvaldaBalanceDifference(item: FinvaldaMismatchInput): number | null {
  const balance = toNumber(item.finvaldaBalance);
  if (balance === null) return null;
  const debt = toNumber(item._debt) ?? 0;
  return balance - debt;
}
